import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Play, X, ListOrdered } from "lucide-react";
import { STEPS, presenter, demo } from "@/lib/demo";
import { speech } from "@/lib/speech";

// Floating presenter console: one click runs each demo step on the right page.
export const PresenterBar = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [on, setOn] = useState(presenter.on());
  const [i, setI] = useState(presenter.step());
  const [list, setList] = useState(false);
  const queued = useRef(null);

  useEffect(() => {
    if (new URLSearchParams(search).get("presenter") === "1" && !presenter.on()) {
      presenter.start();
      setOn(true);
      setI(0);
    }
  }, [search]);

  const run = useCallback((n) => {
    const st = STEPS[n];
    presenter.setStep(n);
    setI(n);
    setList(false);
    if (st.action === "voice") speech.unlock(); // keep inside the click so mobile allows audio
    const cmd = st.action ? { action: st.action, prompt: st.prompt } : null;
    if (pathname !== st.path) {
      // Router updates location in a transition; wait for the new page so the widget sends the right page context.
      queued.current = cmd && { ...cmd, path: st.path };
      navigate(st.path);
    } else if (cmd) {
      demo.run(cmd);
    }
  }, [navigate, pathname]);

  useEffect(() => {
    const q = queued.current;
    if (!q || q.path !== pathname) return;
    queued.current = null;
    demo.run({ action: q.action, prompt: q.prompt });
  }, [pathname]);

  useEffect(() => {
    if (!on) return undefined;
    const onKey = (e) => {
      if (e.altKey && e.key === "ArrowRight") { e.preventDefault(); run(Math.min(i + 1, STEPS.length - 1)); }
      if (e.altKey && e.key === "ArrowLeft") { e.preventDefault(); run(Math.max(i - 1, 0)); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [on, i, run]);

  if (!on || pathname === "/pitch" || pathname === "/embed") return null;
  const st = STEPS[i];
  const exit = () => {
    presenter.stop();
    setOn(false);
    // Drop ?presenter=1 so a reload doesn't silently restart presenter mode.
    if (new URLSearchParams(search).get("presenter") === "1") navigate(pathname, { replace: true });
  };

  return (
    <aside aria-label="Presenter mode" data-testid="presenter-bar"
      className="fixed bottom-6 left-6 z-[60] w-[340px] max-w-[calc(100vw-48px)] border-2 border-white bg-hv-navy text-white shadow-[0_18px_50px_rgba(17,26,50,0.45)]">
      <header className="flex items-center justify-between border-b border-white/15 px-4 py-2">
        <p className="font-heading text-[10px] font-semibold uppercase tracking-[0.2em] text-hv-orange">Presenter · Step {i + 1} of {STEPS.length}</p>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setList(!list)} aria-expanded={list} aria-label="All steps" className="p-1 text-white/80 hover:text-white" data-testid="presenter-steps-toggle"><ListOrdered size={15} /></button>
          <button type="button" onClick={exit} aria-label="Exit presenter mode" className="p-1 text-white/80 hover:text-white" data-testid="presenter-exit-button"><X size={15} /></button>
        </div>
      </header>
      {list ? (
        <ol className="max-h-[300px] overflow-y-auto py-1" data-testid="presenter-step-list">
          {STEPS.map((x, n) => (
            <li key={x.title}>
              <button type="button" onClick={() => run(n)} data-testid={`presenter-step-${n}`}
                className={`flex w-full gap-3 px-4 py-1.5 text-left text-[12.5px] hover:bg-white/10 ${n === i ? "text-hv-orange" : "text-white/85"}`}>
                <span className="w-5 font-heading font-bold">{n + 1}</span>{x.title}
              </button>
            </li>
          ))}
        </ol>
      ) : (
        <div className="px-4 py-3">
          <h2 className="font-heading text-[15px] font-bold" data-testid="presenter-step-title">{st.title}</h2>
          {st.prompt && <p className="mt-1 font-heading text-[11px] text-hv-orange">“{st.prompt}”</p>}
          <p className="mt-2 text-[12.5px] leading-relaxed text-white/80" data-testid="presenter-talking-point">{st.say}</p>
        </div>
      )}
      <footer className="flex items-center gap-2 border-t border-white/15 px-4 py-2.5">
        <button type="button" onClick={() => run(Math.max(i - 1, 0))} disabled={i === 0} aria-label="Previous step" data-testid="presenter-prev-button"
          className="flex h-8 w-8 items-center justify-center border border-white/40 hover:bg-white/10 disabled:opacity-30"><ChevronLeft size={16} /></button>
        <button type="button" onClick={() => run(i)} data-testid="presenter-run-button"
          className="flex h-8 flex-1 items-center justify-center gap-2 bg-white font-heading text-[11px] font-semibold uppercase tracking-[0.14em] text-hv-navy hover:bg-hv-section">
          <Play size={13} aria-hidden="true" /> Run step
        </button>
        <button type="button" onClick={() => run(Math.min(i + 1, STEPS.length - 1))} disabled={i === STEPS.length - 1} aria-label="Next step and run it" data-testid="presenter-next-button"
          className="flex h-8 w-8 items-center justify-center border border-white/40 hover:bg-white/10 disabled:opacity-30"><ChevronRight size={16} /></button>
      </footer>
      <p className="px-4 pb-2 text-[10px] text-white/50">Alt + arrow keys move between steps</p>
    </aside>
  );
};
